import { Decimal } from 'decimal.js';

import { ActivityEntityName, CodeInvoiceStatus, Company, Contact, EmbeddedCode, Entity, Invoice, InvoiceBillingCompany, InvoiceContact, InvoiceLine, InvoiceStatus, InvoiceType, Job, PrepaymentSetting, QuotePro, TaxInclusiveOption, TaxRate } from '../entities';
import { CodeUtility } from './code.utility';
import { DateUtility } from './date.utility';
import { InvoiceUtility } from './invoice.utility';

export type PPInvoiceRequest = {

    source: ActivityEntityName.QuotePro | ActivityEntityName.Job
    quote?: QuotePro,
    job?: Job,

    currentUser?: Entity,
    account: Contact,
    company: Company,
    prepaymentSetting?: PrepaymentSetting,
    taxRate?: TaxRate,
}

export class PrepaymentInvoiceUtility {

    public static build(request: PPInvoiceRequest): Invoice {

        const { quote, job, company, account, source, currentUser } = request;

        const contact = this.getInvoiceContact(account);
        const billingCompany = this.getInvoiceBillingCompany(company);
        const prepaymentSetting = quote?.policy?.prepayments?.settings ?? request.prepaymentSetting;

        const taxInclusiveOption = TaxInclusiveOption.Inclusive;

        let dueDate: Date = null;
        let lines = [];
        let timezone = DateUtility.getLocalTimeZone();
        let today = DateUtility.getDateTime(new Date(), timezone).startOf('day');
        let jobEntity = null;
        let jobAddress = null;
        let taxRate = null;


        switch (source) {
            case ActivityEntityName.QuotePro: {
                taxRate = prepaymentSetting?.saleTaxRate;
                timezone = quote?.address?.timezone;
                dueDate = quote?.expiryDate;

                const safePrepaymentInc = (quote.prepaymentInc as any) == ''
                    ? 0
                    : quote.prepaymentInc;

                const safeOverridePrepaymentInc = (quote.overridePrepaymentInc as any) == ''
                    ? 0
                    : quote.overridePrepaymentInc;

                const safePrepayment = safeOverridePrepaymentInc > 0
                    ? safeOverridePrepaymentInc
                    : safePrepaymentInc;


                const code = CodeUtility.getPrepaymentInvoiceCode(prepaymentSetting, prepaymentSetting?.name, `Prepayment for ${quote.quoteNumber}`, safePrepayment);
                const line = this.newLine(taxInclusiveOption, taxRate, prepaymentSetting, code);
                lines = [line];

                break;
            }

            case ActivityEntityName.Job: {
                taxRate = request.taxRate;
                if (job) {
                    jobEntity = {
                        _id: job._id,
                        name: job.jobNumber,
                    }
                    jobAddress = job.site?.address;
                }

                timezone = job?.site?.address?.timezone ?? timezone;
                today = DateUtility.getDateTime(new Date(), timezone).startOf('day');
                dueDate = InvoiceUtility.getDueDateFromAccount(account?.credit?.dueDate, today).toJSDate();

                const line = this.newLine(taxInclusiveOption, request.taxRate, prepaymentSetting)
                lines = [line];

                break;
            }

            default:
                const line = this.newLine(taxInclusiveOption, request.taxRate, prepaymentSetting)
                lines = [line];
                break;
        }


        // calculate totals
        const invoiceTotalInfo = this.getTotalInfo(lines, taxRate, taxInclusiveOption);

        // build invoice
        const inv: Invoice = {
            _id: undefined,
            invoiceNumber: undefined,
            type: InvoiceType.PrepaymentInvoice,
            invoiceDate: today.toJSDate(),
            contact,
            billingCompany,
            active: true,
            dueDate: dueDate,
            status: InvoiceStatus.Approved,
            lines,

            taxRate,
            taxInclusiveOption,

            subTotal: 0,
            tax: 0,
            total: 0,
            amountPaid: 0,
            outstandingAmount: 0,

            ...invoiceTotalInfo,

            timezone,
            job: jobEntity,
            jobAddress,

            createdBy: currentUser,

            notes: undefined,
            customerReference: undefined,
            terms: undefined,
        }

        return inv;
    }


    /** If there is no code then return empty line */
    public static newLine(taxInclusiveOption: TaxInclusiveOption, taxRate: TaxRate, setting: PrepaymentSetting, code?: EmbeddedCode): InvoiceLine {

        if (!code) {
            const emptyLine: InvoiceLine = {
                _id: undefined,
                name: setting?.name ?? "Prepayment",
                description: null,
                quantity: 1,
                price: 0,
                subTotal: 0,
                tax: 0,
                total: 0,
                taxRate,
                codeInvoicingStatus: CodeInvoiceStatus.Billable,
            }
            return emptyLine;
        }

        const {
            codeInvoicingStatus, quantity, sellPrice, name,
            description, note, location, status, uom, codeUoM, type
        } = code;

        const codeRate = (taxRate?.rate ?? 0) / 100;

        const noCharge = codeInvoicingStatus === CodeInvoiceStatus.NoCharge;
        const qty = quantity ?? 1;
        const price = noCharge
            ? 0
            : new Decimal(sellPrice ?? 0).toDP(4).toNumber();

        const lineSubTotal = new Decimal(qty * price).toDP(2).toNumber();

        let lineTax = 0;
        let lineTotal = 0;

        switch (taxInclusiveOption) {
            case TaxInclusiveOption.Exclusive:
                lineTax = new Decimal(Number(lineSubTotal * codeRate)).toDP(2).toNumber();
                lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();
                break;

            case TaxInclusiveOption.Inclusive:
                const taxPlus = 1 + codeRate;
                lineTax = new Decimal((lineSubTotal / taxPlus) * codeRate).toDP(2).toNumber();
                lineTotal = lineSubTotal;
                break;
        }

        const line: InvoiceLine = {
            _id: undefined,
            name,
            description,
            notes: note,
            location,
            codeStatus: status,
            quantity: qty,
            price: price,
            subTotal: lineSubTotal,
            tax: lineTax,
            total: lineTotal,
            uom,
            codeUoM,
            // itemLink: itemLink,
            // glCode,
            taxRate,
            type,
            codeInvoicingStatus,
        }

        return line

    }


    /** return invoice total info from lines */
    public static getTotalInfo(lines: InvoiceLine[], defaultTaxRate: TaxRate, taxInclusiveOption: TaxInclusiveOption): Pick<Invoice, 'lines' | 'prepaymentSubTotal' | 'prepaymentTax' | 'prepaymentTotal'> {

        let result = {
            lines: [],
            prepaymentSubTotal: 0,
            prepaymentTax: 0,
            prepaymentTotal: 0,
        }

        if (!lines || lines.length === 0) return result;

        result = lines
            .filter(line => !!line)
            .reduce((acc, line) => {

                // line rate > default rate > 0
                const codeRate = (line.taxRate?.rate ?? defaultTaxRate?.rate ?? 0) / 100;

                const price = new Decimal(line.price ?? 0).toDP(4).toNumber();
                const lineSubTotal = new Decimal(line.quantity * price).toDP(2).toNumber();

                let lineTax = 0;
                let lineTotal = 0;

                switch (taxInclusiveOption) {
                    case TaxInclusiveOption.Inclusive:
                        lineTax = new Decimal((lineSubTotal / (1 + codeRate)) * codeRate).toDP(2).toNumber();
                        lineTotal = lineSubTotal
                        break;
                    case TaxInclusiveOption.Exclusive:
                        lineTax = new Decimal(Number(lineSubTotal * codeRate)).toDP(2).toNumber();
                        lineTotal = new Decimal(lineSubTotal + lineTax).toDP(2).toNumber();
                        break;
                }

                line.subTotal = lineSubTotal;
                line.tax = lineTax;
                line.total = lineTotal;

                acc.prepaymentSubTotal += lineSubTotal;
                acc.prepaymentTax += lineTax;
                acc.prepaymentTotal += lineTotal;
                acc.lines.push(line);

                return acc;
            }, result);

        return result;

    }


    ///////////// ///////////// ///////////// ///////////// /////////////


    public static getInvoiceBillingCompany(company: Company): InvoiceBillingCompany {

        if (!company) return null;

        const { _id, name, address, phone, email, taxId, invoiceDetails } = company;

        const billingCompany: InvoiceBillingCompany = {
            _id,
            name,
            address,
            phone,
            email,
            taxId,
            invoiceDetails,
            taxRate: undefined
        };

        return billingCompany;
    }


    public static getInvoiceContact(account: Contact): InvoiceContact {

        if (!account) return null;

        const invContact = InvoiceUtility.getInvoiceContact(account, null);

        return invContact;
    }

}
